import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    CheckCircle, AlertTriangle, Leaf, X, Search, Plus, Trash,
    Eye, EyeOff, ArrowLeft, Save, Info, Calendar, MapPin, 
    Image as ImageIcon, Sparkles, Wand2
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import LocationMap from '../components/common/LocationMap';

export default function HerbariumEditor({ plants, categories }) {
    const { id } = useParams();
    const isNew = id === 'new';
    const navigate = useNavigate();
    const { fetchWithAuth } = useContext(AuthContext);
    const { showToast: addToast } = useToast();

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [magicFilling, setMagicFilling] = useState(false);
    const [plantSearchQuery, setPlantSearchQuery] = useState('');
    const [showPlantResults, setShowPlantResults] = useState(false);
    
    const [entry, setEntry] = useState({
        plant: '',
        captured_at: new Date().toISOString().split('T')[0],
        location: '',
        latitude: null,
        longitude: null,
        show_map: true,
        form_data: {
            botanical_name: '',
            german_name: '',
            family: '',
            category: '',
            origin: '',
            observed_location: '',
            datum: new Date().toLocaleDateString('de-DE'),
        },
        image: null,
        image_preview: null
    });

    useEffect(() => {
        if (!isNew) {
            fetchEntry();
        }
    }, [id]);

    const fetchEntry = async () => {
        try {
            const response = await fetchWithAuth(`/api/apprentice/herbarium/${id}/`);
            if (response.ok) {
                const data = await response.json();
                
                if (data) {
                    const mergedData = {
                        ...data,
                        image_preview: data.image,
                        form_data: {
                            ...entry.form_data,
                            ...(data.form_data || {})
                        }
                    };

                    // AUTOFILL: If plant is linked but form_data fields are empty, pull from master list
                    if (data.plant && (!mergedData.form_data.botanical_name)) {
                        const masterPlant = plants.find(p => p.id === data.plant);
                        if (masterPlant) {
                            mergedData.form_data.botanical_name = masterPlant.botanical_name;
                            mergedData.form_data.german_name = masterPlant.german_name;
                            mergedData.form_data.family = masterPlant.family;
                            mergedData.form_data.category = masterPlant.category_name || 
                                (typeof masterPlant.category === 'string' ? masterPlant.category : masterPlant.category?.name);
                        }
                    }

                    setEntry(mergedData);
                    
                    if (mergedData.form_data?.botanical_name) {
                        setPlantSearchQuery(mergedData.form_data.botanical_name);
                    } else if (data.plant_name) {
                        setPlantSearchQuery(data.plant_name);
                    }
                }
            }
        } catch (error) {
            addToast('Fehler beim Laden des Eintrags', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (field, value) => {
        setEntry(prev => ({
            ...prev,
            form_data: {
                ...prev.form_data,
                [field]: value
            }
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEntry(prev => ({
                ...prev,
                image: file,
                image_preview: URL.createObjectURL(file)
            }));
        }
    };

    const handleMagicFill = async () => {
        if (!entry.plant) {
            addToast('Bitte zuerst eine Pflanze auswählen', 'info');
            return;
        }

        setMagicFilling(true);
        try {
            // Reusing updated research logic
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/research_for_plant/`, {
                method: 'POST',
                body: JSON.stringify({ plant_id: entry.plant })
            });

            if (response.ok) {
                const data = await response.json();
                const suggestions = data.form_data || {};
                
                // Keep current captured_at/location if they exist
                setEntry(prev => ({
                    ...prev,
                    form_data: {
                        ...prev.form_data,
                        ...suggestions,
                        botanical_name: suggestions.botanical_name || prev.form_data.botanical_name,
                        german_name: suggestions.german_name || prev.form_data.german_name,
                        family: suggestions.family || prev.form_data.family,
                        category: suggestions.category || suggestions.growth_form || prev.form_data.category,
                        origin: suggestions.origin || prev.form_data.origin,
                        observed_location: prev.location || suggestions.observed_location || prev.form_data.observed_location,
                        datum: prev.captured_at || suggestions.datum || prev.form_data.datum,
                    }
                }));
                addToast('Magix Fill abgeschlossen!', 'success');
            }
        } catch (error) {
            addToast('KI-Befüllung fehlgeschlagen', 'error');
        } finally {
            setMagicFilling(false);
        }
    };

    const handleRemoveImage = (e) => {
        if (e) e.stopPropagation();
        setEntry(prev => ({
            ...prev,
            image: null,
            image_preview: null
        }));
    };

    const toggleImageVisibility = (e) => {
        if (e) e.stopPropagation();
        setEntry(prev => ({
            ...prev,
            form_data: {
                ...prev.form_data,
                show_image: prev.form_data.show_image === false ? true : false
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('plant', entry.plant || '');
            formData.append('captured_at', entry.captured_at);
            formData.append('location', entry.location);
            formData.append('latitude', entry.latitude || '');
            formData.append('longitude', entry.longitude || '');
            formData.append('show_map', entry.show_map ?? true);
            formData.append('form_data', JSON.stringify(entry.form_data));
            if (entry.image instanceof File) {
                formData.append('image', entry.image);
            }

            const url = isNew ? '/api/apprentice/herbarium/' : `/api/apprentice/herbarium/${id}/`;
            const method = isNew ? 'POST' : 'PATCH';

            const response = await fetchWithAuth(url, {
                method,
                body: formData,
                headers: {
                    // Let fetchWithAuth use auto content-type for FormData
                }
            });

            if (response.ok) {
                addToast('Erfolgreich gespeichert', 'success');
                navigate('/apprentice/herbarium');
            }
        } catch (error) {
            addToast('Fehler beim Speichern', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
            <div className="w-12 h-12 border-4 border-plantlix-500/20 border-t-plantlix-500 rounded-full animate-spin"></div>
            <p className="text-gray-500 font-medium">Lade Editor...</p>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Header / Actions */}
            <div className="flex items-center justify-between mb-8">
                <button 
                    onClick={() => navigate('/apprentice/herbarium')}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>Zurück</span>
                </button>
                
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2.5 bg-plantlix-500 hover:bg-plantlix-600 disabled:opacity-50 text-white rounded-xl font-bold transition-all shadow-md"
                    >
                        {saving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-5 h-5" />}
                        <span>Speichern</span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Left Form: Data */}
                <div className="lg:col-span-3 space-y-6">
                    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                            <Info className="w-5 h-5 text-plantlix-500" />
                            Herbar-Stammdaten
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2 space-y-2">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Pflanze aus Bestiarium (optional)</label>
                                <div className="relative">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input 
                                            type="text"
                                            placeholder="Nach Pflanze suchen..."
                                            value={plantSearchQuery}
                                            onChange={(e) => {
                                                setPlantSearchQuery(e.target.value);
                                                setShowPlantResults(true);
                                            }}
                                            onFocus={() => setShowPlantResults(true)}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-plantlix-500"
                                        />
                                        {entry.plant && (
                                            <button 
                                                onClick={() => {
                                                    setEntry(prev => ({ ...prev, plant: '' }));
                                                    setPlantSearchQuery('');
                                                }}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>

                                    {showPlantResults && plantSearchQuery.length > 0 && (
                                        <div className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-gray-700 rounded-xl shadow-xl max-h-60 overflow-y-auto overflow-x-hidden">
                                            {plants
                                                .filter(p => 
                                                    p.botanical_name.toLowerCase().includes(plantSearchQuery.toLowerCase()) || 
                                                    (p.german_name || '').toLowerCase().includes(plantSearchQuery.toLowerCase())
                                                )
                                                .slice(0, 10)
                                                .map(p => (
                                                    <div 
                                                        key={p.id}
                                                        onClick={() => {
                                                            setEntry(prev => ({ 
                                                                ...prev, 
                                                                plant: p.id,
                                                                form_data: {
                                                                    ...prev.form_data,
                                                                    botanical_name: p.botanical_name || prev.form_data.botanical_name,
                                                                    german_name: p.german_name || prev.form_data.german_name,
                                                                    family: p.family || prev.form_data.family,
                                                                    category: p.category_name || (typeof p.category === 'string' ? p.category : p.category?.name) || prev.form_data.category
                                                                }
                                                            }));
                                                            setPlantSearchQuery(p.botanical_name);
                                                            setShowPlantResults(false);
                                                        }}
                                                        className="px-4 py-3 hover:bg-plantlix-50 dark:hover:bg-plantlix-900/20 cursor-pointer transition-colors border-b border-gray-50 dark:border-gray-800 last:border-none"
                                                    >
                                                        <div className="font-bold text-sm text-gray-900 dark:text-white">{p.botanical_name}</div>
                                                        <div className="text-xs text-gray-500">{p.german_name || '-'}</div>
                                                    </div>
                                                ))}
                                            {plants.filter(p =>  p.botanical_name.toLowerCase().includes(plantSearchQuery.toLowerCase()) || (p.german_name || '').toLowerCase().includes(plantSearchQuery.toLowerCase())).length === 0 && (
                                                <div className="px-4 py-6 text-center text-gray-500 text-sm italic">
                                                    Keine Pflanze gefunden...
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Funddatum</label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input 
                                        type="date"
                                        value={entry.captured_at}
                                        onChange={(e) => setEntry(prev => ({ ...prev, captured_at: e.target.value }))}
                                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-plantlix-500"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Fundort</label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input 
                                        type="text"
                                        placeholder="z.B. Stadtpark"
                                        value={entry.location}
                                        onChange={(e) => setEntry(prev => ({ ...prev, location: e.target.value }))}
                                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-plantlix-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Map Section for Herbarium */}
                        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-4">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-plantlix-500" /> Fundort auf Karte
                                </label>
                                <button 
                                    onClick={() => setEntry(prev => ({ ...prev, show_map: !prev.show_map }))}
                                    className={`flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black transition-all ${entry.show_map ? 'bg-plantlix-500 text-white' : 'bg-gray-600 text-white'}`}
                                >
                                    {entry.show_map ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                    {entry.show_map ? 'IM PDF' : 'NICHT IM PDF'}
                                </button>
                            </div>
                            <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                <LocationMap 
                                    lat={entry.latitude}
                                    lng={entry.longitude}
                                    isEditable={true}
                                    onLocationSelect={(lat, lng) => setEntry(prev => ({ ...prev, latitude: lat, longitude: lng }))}
                                    height="180px"
                                    zoom={12}
                                />
                            </div>
                            <p className="text-[10px] text-center text-gray-400 mt-2 font-medium italic">
                                Tipp: Klicke auf die Karte, um den Fundort genau zu markieren.
                            </p>
                        </div>

                        <div className="mt-8 pt-8 border-t border-gray-100 dark:border-gray-800">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="font-bold text-gray-900 dark:text-white">Dokument-Inhalte</h3>
                                <button 
                                    onClick={handleMagicFill}
                                    disabled={magicFilling || !entry.plant}
                                    className="flex items-center gap-2 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 rounded-lg text-xs font-bold transition-all disabled:opacity-50"
                                >
                                    {magicFilling ? <Sparkles className="w-4 h-4 animate-pulse" /> : <Wand2 className="w-4 h-4" />}
                                    <span>MAGIX FILL</span>
                                </button>
                            </div>

                            <div className="space-y-4">
                                {[
                                    { id: 'botanical_name', label: 'Botanischer Name' },
                                    { id: 'german_name', label: 'Deutscher Name' },
                                    { id: 'family', label: 'Familie' },
                                    { id: 'category', label: 'Einordnung (Kategorie)' },
                                    { id: 'origin', label: 'Heimat' },
                                ].map(field => (
                                    <div key={field.id} className="space-y-1.5">
                                        <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">{field.label}</label>
                                        <input 
                                            type="text"
                                            value={entry.form_data[field.id] || ''}
                                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                            className="w-full px-4 py-2.5 bg-white dark:bg-[#1e1e1e] border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-plantlix-500 outline-none transition-all text-sm"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Image/Scanner */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                            <ImageIcon className="w-5 h-5 text-plantlix-500" />
                            Herbar-Bild (Optional)
                        </h2>

                        <div 
                            onClick={() => document.getElementById('image-upload').click()}
                            className="aspect-[3/4] rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all overflow-hidden relative"
                        >
                            {entry.image_preview ? (
                                <>
                                    <img 
                                        src={entry.image_preview} 
                                        className={`w-full h-full object-contain transition-opacity ${entry.form_data.show_image === false ? 'opacity-30 grayscale' : ''}`} 
                                        alt="Preview" 
                                    />
                                    <div className="absolute top-4 right-4 flex gap-2">
                                        <button 
                                            onClick={toggleImageVisibility}
                                            className={`p-2 rounded-lg shadow-lg transition-all ${entry.form_data.show_image === false ? 'bg-gray-500 text-white' : 'bg-plantlix-500 text-white'}`}
                                            title={entry.form_data.show_image === false ? "Im PDF anzeigen" : "Im PDF ausblenden"}
                                        >
                                            {entry.form_data.show_image === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                        <button 
                                            onClick={handleRemoveImage}
                                            className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg shadow-lg transition-colors"
                                            title="Bild löschen"
                                        >
                                            <Trash className="w-4 h-4" />
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <ImageIcon className="w-12 h-12 text-gray-300 mb-2" />
                                    <p className="text-sm font-bold text-gray-400">Bild hochladen</p>
                                    <p className="text-[10px] text-gray-400 mt-1">PNG, JPG bis 10MB</p>
                                </>
                            )}
                            <input 
                                id="image-upload"
                                type="file" 
                                hidden 
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>
                    </div>

                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-6 border border-amber-100 dark:border-amber-800/30">
                        <div className="flex gap-4">
                            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                            <div>
                                <h4 className="text-amber-800 dark:text-amber-400 font-bold text-sm">Pro-Tipp: Digitaler Scan</h4>
                                <p className="text-amber-700/80 dark:text-amber-500/80 text-xs mt-1 leading-relaxed">
                                    Für das beste Ergebnis fotografiere deine gepresste Pflanze auf einem weißen Blatt Papier bei gutem Tageslicht.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
